import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import { Device } from "@capacitor/device";
import { App } from "@capacitor/app";
import { PushNotifications } from "@capacitor/push-notifications";
import { SplashScreen } from "@capacitor/splash-screen";
import { StatusBar, Style } from "@capacitor/status-bar";
import { Keyboard, KeyboardResize, KeyboardStyle } from "@capacitor/keyboard";
import { Network } from "@capacitor/network";
import { router } from "@inertiajs/vue3";
import axios from "axios";
import { createApp } from "vue";
import OfflineScreen from "@/Components/OfflineScreen.vue";
import i18n from "@/Plugins/i18n.js";
import { eventBus } from "@/Helpers/eventbus.js";
import { closeTopOverlay } from "@/Composables/useBackDismiss.js";
import { TOAST_STATES } from "@/Helpers/constants/account.js";
import { initCounts, markNotificationAsRead } from "@/Helpers/leanplumInbox.js";
import {
    resumeIncompleteAltBillingPurchase,
    resumePendingAltBillingPurchases,
} from "@/Helpers/playAlternativeBilling.js";
import {
    resumeIncompleteExternalPurchase,
    resumePendingExternalPurchases,
} from "@/Helpers/externalPurchase.js";

export const isNative = () => Capacitor.isNativePlatform();
export const isIos = () => Capacitor.getPlatform() === "ios";
export const isAndroid = () => Capacitor.getPlatform() === "android";

export const onKeyboardChange = (callback) => {
    if (!isNative()) return;

    Keyboard.addListener("keyboardWillShow", ({ keyboardHeight }) =>
        callback(keyboardHeight)
    );
    Keyboard.addListener("keyboardWillHide", () => callback(0));
};

export const openExternalURL = async (url) => {
    if (Capacitor.isNativePlatform()) {
        await Browser.open({ url });
    } else if (window.natively) {
        window.natively.openExternalURL(url, true);
    } else {
        window.open(url, "_blank")?.focus();
    }
};

export const sendEmail = (subject, body, recipient) => {
    const url = `mailto:${recipient}?subject=${encodeURIComponent(
        subject
    )}&body=${encodeURIComponent(body)}`;
    if (Capacitor.isNativePlatform()) {
        window.location.href = url;
    } else if (window.natively) {
        const message = new window.NativelyMessage();
        message.sendEmail(subject, body, recipient);
    } else {
        window.open(url, "_blank");
    }
};

export const registerPushNotifications = async () => {
    if (!Capacitor.isNativePlatform()) {
        return;
    }

    const { receive } = await PushNotifications.requestPermissions();
    if (receive !== "granted") {
        return;
    }

    PushNotifications.addListener("registration", async (token) => {
        await axios.post(route("internals.mobile.push.token"), {
            token: token.value,
            platform: Capacitor.getPlatform(),
        });
    });

    await PushNotifications.register();

    PushNotifications.addListener(
        "pushNotificationReceived",
        (notification) => {
            const deliveryId = notification.data?.["CIO-Delivery-ID"];
            if (deliveryId) {
                axios.post(route("internals.track.push.metric"), {
                    delivery_id: deliveryId,
                    metric: "delivered",
                });
            }
        }
    );

    PushNotifications.addListener(
        "pushNotificationActionPerformed",
        (action) => {
            const data = action.notification.data ?? {};

            const deliveryId = data["CIO-Delivery-ID"];
            if (deliveryId) {
                axios.post(route("internals.track.push.metric"), {
                    delivery_id: deliveryId,
                    metric: "opened",
                });
            }

            const notification_id = data.notification_id;
            if (notification_id) {
                router.post(
                    route(
                        "internals.read.single.notification",
                        notification_id
                    ),
                    {},
                    {
                        onFinish: () => {
                            markNotificationAsRead(notification_id);
                            initCounts();
                        },
                    }
                );
            }
        }
    );
};

export const handleOauthSuccessUrl = async (url) => {
    const token = url.searchParams.get("token");
    if (token) {
        try {
            await axios.post(route("mobile.login.exchange"), { token });
        } catch {
            router.visit(route("app.landing"));
            return;
        }
    }
    router.visit(route("dashboard"));
};

export const handleOauthErrorUrl = (url) => {
    const reason = url.searchParams.get("reason");
    eventBus.flash.toast = {
        message:
            reason === "2"
                ? "auth.social_login.account_exists"
                : "auth.social_login.no_account",
        status: TOAST_STATES.ERROR,
    };
};

const handleNativeBack = (canGoBack) => {
    if (closeTopOverlay()) return true;

    if (eventBus.backNavigationHandler) {
        eventBus.backNavigationHandler();
        return true;
    }

    if (canGoBack) {
        window.history.back();
        return true;
    }

    return false;
};

export const initNative = () => {
    if (!Capacitor.isNativePlatform()) return;

    App.addListener("appUrlOpen", async (event) => {
        let url;
        try {
            url = new URL(event.url);
        } catch {
            return;
        }

        const protocol = url.protocol.toLowerCase();

        if (
            protocol === "com.bctvrluvkwzm.natively:" ||
            protocol === "com.lemonswan.app:"
        ) {
            await Browser.close();
            if (url.host === "oauth-success") {
                await handleOauthSuccessUrl(url);
            } else if (url.host === "oauth-error") {
                handleOauthErrorUrl(url);
            } else if (url.host === "payment-success") {
                const path = url.searchParams.get("path");
                router.visit(path || route("dashboard"));
            } else if (url.host === "payment-failed") {
                const path = url.searchParams.get("path");
                router.visit(
                    path || route("paywall.single-page-checkout.show")
                );
            }
            return;
        }

        if (protocol === "https:") {
            await Browser.close();
            router.visit(url.pathname + url.search);
            return;
        }

        await Browser.open({ url: event.url });
    });

    sendDeviceInfo().then(() => registerPushNotifications());

    if (Capacitor.getPlatform() === "ios") {
        window.handleNativeBack = handleNativeBack;
        Keyboard.setResizeMode({ mode: KeyboardResize.Body });
        Keyboard.setStyle({ style: KeyboardStyle.Light });
        resumePendingExternalPurchases();
        resumeIncompleteExternalPurchase();
        App.addListener("appStateChange", ({ isActive }) => {
            if (isActive) {
                resumePendingExternalPurchases();
                resumeIncompleteExternalPurchase();
            }
        });
    }

    StatusBar.setStyle({ style: Style.Light });
    if (Capacitor.getPlatform() === "android") {
        StatusBar.setBackgroundColor({ color: "#8DC512" });
        App.addListener("backButton", ({ canGoBack }) => {
            if (!handleNativeBack(canGoBack)) {
                App.exitApp();
            }
        });

        resumePendingAltBillingPurchases();
        resumeIncompleteAltBillingPurchase();
        App.addListener("appStateChange", ({ isActive }) => {
            if (isActive) {
                resumePendingAltBillingPurchases();
                resumeIncompleteAltBillingPurchase();
            }
        });
    }

    Network.addListener("networkStatusChange", (status) => {
        if (status.connected && isAndroid()) {
            resumePendingAltBillingPurchases();
        }

        if (status.connected && isIos()) {
            resumePendingExternalPurchases();
        }

        document.dispatchEvent(
            new CustomEvent("networkStatusChange", { detail: status })
        );
    });

    SplashScreen.hide();

    const container = document.createElement("div");
    document.body.appendChild(container);
    createApp(OfflineScreen).use(i18n).mount(container);
};

export const sendDeviceInfo = async () => {
    const deviceInfo = await getDeviceInfo();
    if (deviceInfo) {
        await axios.post(route("internals.mobile.app.details"), deviceInfo);
    }
};

export const getDeviceInfo = async () => {
    if (!Capacitor.isNativePlatform()) {
        if (window.natively && typeof window.NativelyInfo !== "undefined") {
            const nativelyInfo = new window.NativelyInfo();
            return await new Promise((resolve) => {
                nativelyInfo.getAppInfo((resp) => {
                    resolve({
                        device: resp.device,
                        os_version: resp.osVersion,
                        os_name: "Natively",
                        build_version: resp.build,
                        sdk_version: resp.version,
                    });
                });
            });
        }
        return null;
    }

    const [deviceInfo, appInfo] = await Promise.all([
        Device.getInfo(),
        App.getInfo(),
    ]);

    return {
        device: deviceInfo.model,
        os_version: deviceInfo.osVersion,
        os_name: deviceInfo.operatingSystem,
        build_version: appInfo.build,
        sdk_version: appInfo.version,
    };
};
